import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DialogContext } from '../../../../store/context/DialogContext';
import { doPOST } from '../../../../util/HttpUtil';
import "./styles.css";
import { INPUT_AUTOFILL_PROPS } from '../../../../components/InputField/autofillProps';
import { useTranslation } from '../../../../store/context/TranslationContext';
import PreviousValue from '../../../../components/InputField/PreviousValue';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../../../../components/InputField/testId';
import { MIN_USERNAME_LENGTH, USERNAME_NOTE } from './constants';
const sanitizeUsername = (candidate = '') => String(candidate).replace(/[^a-zA-Z0-9_.]/g, '');
const normalizeUsername = (candidate = '') => sanitizeUsername(candidate || '').trim();

const UserNameField = ({
    id,
    applicationId,
    individual_id,
    label,
    required,
    className,
    style,
    onChange,
    err,
    showErr,
    value,
    handleLoading,
    isValidUsername,
    setIsValidUsername,
    isDisabled = false,
    prevValue,
    description = USERNAME_NOTE,
}) => {

    const [username, setUsername] = useState(() => normalizeUsername(value));
    const [error, setError] = useState('');
    const [hasError, setHasError] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const inputRef = useRef(null);
    const validationRequestIdRef = useRef(0);
    const lastValidatedContextRef = useRef('');
    const latestUsernameRef = useRef(normalizeUsername(value));
    const lastSyncedUsernameRef = useRef(normalizeUsername(value));
    const { showError } = useContext(DialogContext);
    const { t, } = useTranslation();
    const fieldTestId = resolveAutomationTestId(id, label, "username");

    useEffect(() => {
        latestUsernameRef.current = username;
    }, [username]);

    useEffect(() => {
        if ((showErr && err?.length) && !error?.length) {
            setError(err);
        }
    }, [err, error, showErr]);

    useEffect(() => {
        setHasError(showErr);
    }, [showErr]);

    const buildValidationContextKey = useCallback((usernameToCheck) => (
        `${applicationId || ''}:${individual_id || ''}:${String(usernameToCheck || '').trim()}`
    ), [applicationId, individual_id]);

    const commitUsernameChange = useCallback((nextUsername) => {
        const normalizedCommittedUsername = String(value || '').trim();
        if (normalizedCommittedUsername !== nextUsername) {
            onChange(nextUsername);
        }
    }, [onChange, value]);

    const resetPendingValidationState = useCallback(() => {
        validationRequestIdRef.current += 1;
        handleLoading?.(false);
        lastValidatedContextRef.current = '';
        setError('');
        setHasError(false);
        if (typeof setIsValidUsername === 'function') {
            setIsValidUsername(undefined);
        }
    }, [handleLoading, setIsValidUsername]);

    const syncDomUsernameToState = useCallback((rawValue) => {
        const sanitizedValue = normalizeUsername(rawValue);
        if (latestUsernameRef.current !== sanitizedValue) {
            setUsername(sanitizedValue);
        }
        return sanitizedValue;
    }, []);

    const checkUsernameAvailability = useCallback(async (usernameToCheck) => {
        try {
            const payload = {
                username: usernameToCheck,
                applicationId,
                ...(individual_id && { individual_id })
            };

            const response = await doPOST(`/api/username/get`, payload);
            return response?.data ?? [];
        } catch (error) {
            showError(error);
            return [];
        }
    }, [applicationId, individual_id, showError]);

    const runUsernameValidation = useCallback(async (usernameToValidate, { surfaceError = false } = {}) => {
        if (isDisabled) {
            return null;
        }

        const normalizedUsername = String(usernameToValidate || '').trim();
        const validationContextKey = buildValidationContextKey(normalizedUsername);

        if (!normalizedUsername) {
            setError(t('Username cannot be empty.'));
            setHasError(surfaceError);
            commitUsernameChange('');
            if (typeof setIsValidUsername === 'function') {
                setIsValidUsername(false);
            }
            lastValidatedContextRef.current = validationContextKey;
            return false;
        }

        if (normalizedUsername.length < MIN_USERNAME_LENGTH) {
            setError(t(`Username must be at least ${MIN_USERNAME_LENGTH} characters long.`));
            setHasError(surfaceError);
            commitUsernameChange(normalizedUsername);
            if (typeof setIsValidUsername === 'function') {
                setIsValidUsername(false);
            }
            lastValidatedContextRef.current = validationContextKey;
            return false;
        }

        const requestId = validationRequestIdRef.current + 1;
        validationRequestIdRef.current = requestId;
        setIsCheckingUsername(true);
        handleLoading?.(true);

        try {
            const response = await checkUsernameAvailability(normalizedUsername);
            if (requestId !== validationRequestIdRef.current) {
                return null;
            }

            if (String(latestUsernameRef.current || '').trim() !== normalizedUsername) {
                return null;
            }

            const nextIsValidUsername = response?.length > 0;
            setError(nextIsValidUsername ? '' : t('Username is not available.'));
            setHasError(surfaceError && !nextIsValidUsername);
            commitUsernameChange(normalizedUsername);
            if (typeof setIsValidUsername === 'function') {
                setIsValidUsername(nextIsValidUsername);
            }
            lastValidatedContextRef.current = validationContextKey;
            return nextIsValidUsername;
        } finally {
            if (requestId === validationRequestIdRef.current) {
                handleLoading?.(false);
                setIsCheckingUsername(false);
            }
        }
    }, [buildValidationContextKey, checkUsernameAvailability, commitUsernameChange, handleLoading, isDisabled, setIsValidUsername, t]);

    useEffect(() => {
        const normalizedValue = normalizeUsername(value);
        const validationContextKey = buildValidationContextKey(normalizedValue);
        const localUsername = String(latestUsernameRef.current || '').trim();
        const hasUnsyncedLocalValue = localUsername !== lastSyncedUsernameRef.current;
        const shouldAcceptExternalValue = normalizedValue === localUsername || (!isFocused && !hasUnsyncedLocalValue);

        if (!shouldAcceptExternalValue) {
            return;
        }

        if (normalizedValue !== localUsername) {
            setUsername(normalizedValue);
        }
        lastSyncedUsernameRef.current = normalizedValue;

        if (!normalizedValue) {
            lastValidatedContextRef.current = '';
            if (typeof setIsValidUsername === 'function') {
                setIsValidUsername(undefined);
            }
            return;
        }

        if (validationContextKey === lastValidatedContextRef.current) {
            return;
        }

        runUsernameValidation(normalizedValue);
    }, [buildValidationContextKey, isFocused, runUsernameValidation, setIsValidUsername, value]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const syncAutofilledUsername = () => {
            const node = inputRef.current;
            if (!node || document.activeElement === node) {
                return;
            }

            const domUsername = sanitizeUsername(node.value || '').trim();
            if (!domUsername || domUsername === latestUsernameRef.current) {
                return;
            }

            resetPendingValidationState();
            setUsername(domUsername);
        };

        syncAutofilledUsername();

        let attempts = 0;
        const intervalId = window.setInterval(() => {
            syncAutofilledUsername();
            attempts += 1;
            if (attempts >= 20) {
                window.clearInterval(intervalId);
            }
        }, 250);

        const handleWindowFocus = () => {
            window.setTimeout(syncAutofilledUsername, 120);
        };

        window.addEventListener('pageshow', handleWindowFocus);
        window.addEventListener('focus', handleWindowFocus);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('pageshow', handleWindowFocus);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [resetPendingValidationState]);

    const handleUsernameChange = (e) => {
        const inputValue = e.target.value;
        const sanitizedValue = sanitizeUsername(inputValue);
        resetPendingValidationState();
        setUsername(sanitizedValue);
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
        if (isDisabled) {
            return;
        }

        const normalizedUsername = syncDomUsernameToState(inputRef.current?.value ?? username);
        const validationContextKey = buildValidationContextKey(normalizedUsername);

        if (normalizedUsername && validationContextKey === lastValidatedContextRef.current && isValidUsername === true) {
            setError('');
            setHasError(false);
            commitUsernameChange(normalizedUsername);
            return;
        }

        runUsernameValidation(normalizedUsername, { surfaceError: true });
    };

    return (
        <div className={`${className} py-3`} style={{ fontSize: '14px', ...style }} data-testid={fieldTestId}>
            <label htmlFor={id || "username"}
                style={{
                    minWidth: "120px",
                    fontSize: 16,
                    margin: "0px 0px 5px 5px",
                    fontWeight: "500",
                    textAlign: "left",
                    display: 'block',
                    marginBottom: '5px'
                }}>
                {t(label ?? "Username")}
                {required && <font color="red">*</font>}
            </label>

            <input
                ref={inputRef}
                id={id || "username"}
                data-testid={buildAutomationChildTestId(fieldTestId, "input")}
                {...INPUT_AUTOFILL_PROPS}
                type="text"
                value={username}
                disabled={isDisabled}
                maxLength={16}
                onChange={handleUsernameChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                style={{ fontSize: '14px' }}
                placeholder={t('Username')}
                className={`inputfield flex-1 ps-3 user-name-input-field ${prevValue?.has ? "is-changed" : isDisabled ? "is-disabled cursor-disabled" : ""}`}
            />

            {description ? (
                <em className="inputfield__desc d-block" data-testid={buildAutomationChildTestId(fieldTestId, "description")}>{t(description)}</em>
            ) : null}
            {!isDisabled && isCheckingUsername ? <em className="selectfield__info" data-testid={buildAutomationChildTestId(fieldTestId, "loading")}>{t("Validating Username..")}.</em> : null}
            <PreviousValue
                value={prevValue?.value}
                show={prevValue?.has}
                testId={buildAutomationChildTestId(fieldTestId, "previous")}
            />
            {isDisabled ? null : (error?.length && hasError && !isCheckingUsername) ? <em className="selectfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>{t(error)}</em> :
                !username?.length && hasError && !isCheckingUsername ? <em className="selectfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>({t("Username is empty")})</em> :
                    (!isCheckingUsername && !isFocused && isValidUsername && username?.length) ? <em className="selectfield__message" data-testid={buildAutomationChildTestId(fieldTestId, "available")}>{t("Username is available")}</em> : null}
        </div>
    );
};

export default UserNameField;
